// Formas del contrato del servicio (réplica de src/core/types.ts de la app,
// solo lo que el portal consume). Mantener alineadas con ese archivo.

export type MembershipStatus = 'pending' | 'active' | 'blocked';
export type Role = 'driver' | 'admin';
export type CompanyRole = 'office' | 'driver';
export type CompanyStatus = 'pending' | 'active' | 'blocked';
export type TripStatus = 'assigned' | 'active' | 'done' | 'cancelled';
export type Appointment = 'required' | 'notRequired' | 'unknown';

export interface Session {
  uid: string; email: string; displayName: string; role: Role; status: MembershipStatus;
  emailVerified: boolean; inviteValidated: boolean;
  phone?: string;
  companyId?: string; companyName?: string;
  companyRole?: CompanyRole;
  /** Unidad habitual del operador: autollenan tracto/caja al asignarle un viaje. */
  defaultTractorId?: string | null; defaultTrailerId?: string | null;
}

export interface Place {
  id: string; name: string; zone: string; country: string; address: string; hours: string; timeZone: string;
  appointment: Appointment; accessInstructions: string; maneuverNotes: string;
  rampCount: number | null; phone: string; whatsapp: string; contactName: string; contactPhone: string;
  accessCodes: string; observations: string;
  accessPoint?: { latitude: number; longitude: number };
  sitePoint?: { latitude: number; longitude: number };
  accessApprox?: boolean;
  companyId?: string;
  photos: PlacePhoto[];
  reviewedAt: string | null; revision: number;
  stats: { minMinutes: number; maxMinutes: number; visits: number; waitMinutes: number | null; unloadMinutes: number | null } | null;
}

export interface PlacePhoto { id: string; url: string; authorId: string; authorName: string; caption: string; uploadedAt: string }

export interface Contribution {
  id: string; kind: 'edit' | 'newPlace'; placeId: string; authorId: string; authorName: string;
  field: EditableField | 'newPlace'; currentValue: string; proposedValue: string; reason: string;
  draft: PlaceDraft | null;
  baseRevision: number; status: 'pending' | 'approved' | 'rejected';
  createdAt: string; reviewedAt: string | null; reviewNote: string;
}

export interface PlaceDraft {
  name: string; zone: string; country: string; address: string; hours: string; timeZone: string;
  appointment: Appointment; accessInstructions: string; accessCodes: string; maneuverNotes: string;
  rampCount: number | null; phone: string; whatsapp: string; contactName: string; observations: string;
  contactPhone?: string;
  accessPoint: { latitude: number; longitude: number } | null;
  sitePoint?: { latitude: number; longitude: number } | null;
}

export const EDITABLE_FIELDS = ['name', 'zone', 'country', 'address', 'hours', 'accessInstructions', 'accessCodes', 'maneuverNotes', 'phone', 'whatsapp', 'contactName', 'contactPhone', 'observations', 'accessPoint', 'sitePoint'] as const;
export type EditableField = typeof EDITABLE_FIELDS[number];

export const FIELD_LABELS: Record<EditableField, string> = {
  name: 'Nombre', zone: 'Zona o ciudad', country: 'País', address: 'Dirección', hours: 'Horario de recepción',
  accessInstructions: 'Entrada para camiones', accessCodes: 'Claves y códigos', maneuverNotes: 'Maniobra',
  phone: 'Teléfono', whatsapp: 'WhatsApp', contactName: 'Contacto de recepción', contactPhone: 'Teléfono del contacto', observations: 'Observaciones',
  accessPoint: 'Entrada del camión en el mapa', sitePoint: 'Ubicación del lugar en el mapa',
};

export interface AuditEvent { id: string; action: string; actorId: string; subjectId: string; note: string; createdAt: string; placeId?: string; field?: string; proposedValue?: string;
  /** Extras que serializa el backend nuevo (deep-links del feed del Tablero,
      origen del evento, favoritos): si faltan, los links simplemente no se
      muestran — degrada limpio. */
  unitIds?: string[]; source?: string; tripId?: string; visitId?: string; favorite?: boolean }

export interface Visit {
  id: string; placeId: string; driverId: string; driverName: string;
  arrivedAt: string; doorAssignedAt: string | null; unloadStartAt: string | null; unloadEndAt: string | null;
  countedAt: string | null; signedAt: string | null; departedAt: string | null;
  door: string; doorSelfChosen: boolean; note: string; completed: boolean; abandoned?: boolean; autoClosed?: boolean; createdAt: string;
  photos: PlacePhoto[];
  deliveredPallets: number; deliveredPartials: number; deliveredBoxes: number;
}

export interface Company { id: string; name: string; status: CompanyStatus; memberCount: number; createdAt: string }
export interface CompanyInfo { id: string; name: string; role: CompanyRole; status: CompanyStatus; customUnitKinds?: CustomUnitKind[] }

/** Nota privada de la empresa sobre un lugar (`companies/<cid>/notes`) —
    mismo shape que CompanyNote de src/core/types.ts de la app. */
export interface CompanyNote { id: string; placeId: string; body: string; authorId: string; authorName: string; createdAt: string; updatedAt: string }

export interface TripStop {
  placeId: string; pallets: number; partialPallets: number; looseBoxes: number; note: string;
  /**
   * Progreso que el SERVIDOR infiere de las visitas del chofer en este lugar
   * desde que se asignó el viaje (solo viajes abiertos): visiting = visita en
   * curso, done = visita cerrada; delivered = piezas entregadas registradas.
   * Así la oficina ve en qué parada va el chofer sin que el chofer reporte nada.
   */
  progress?: { status: 'visiting' | 'done'; at: string; delivered: number } | null;
}
export interface Trip { id: string; driverId: string; driverName: string; stops: TripStop[]; notes: string; status: TripStatus; createdBy: string; createdByName: string; createdAt: string; updatedAt: string;
  /** Unidades del viaje (opcionales): tracto y caja asignados; el reefer cuelga de la caja vía parentUnitId. */
  tractorUnitId?: string | null; trailerUnitId?: string | null;
  /** Salida con defectos graves autorizada por la oficina: quién autorizó,
      cuándo y la nota — el viaje arranca aunque el reporte siga abierto. */
  defectOverride?: { by: string; byName: string; note: string; at: string | null } | null;
  /** El chofer bloqueado pidió a la oficina que autorice su salida con
      defectos graves; se limpia al autorizar. */
  defectAuthRequest?: { by: string; byName: string; at: string | null } | null;
  /** Documentos adjuntos del viaje (tripDocs): el servidor los cuenta al
      serializar el viaje (default 0); en snapshots de backends viejos puede
      llegar ausente — en la vista se lee con `?? 0`. */
  tripDocsCount: number }

// ---- Unidades y documentos de la empresa (réplica de src/core/types.ts) ----

/** Categoría funcional: vehículos se mueven solos, remolques se remolcan,
    equipo cuelga de un remolque. Los viajes piden vehicle+attachment. */
export type UnitCategory = 'vehicle' | 'attachment' | 'equipment';
/** Tipos integrados; los de la empresa llegan como 'ck-<slug>'. */
export type BuiltinUnitKind =
  | 'tractor' | 'camion' | 'tortuga' | 'van'
  | 'trailer' | 'cajaRefrigerada' | 'plataforma' | 'lowboy' | 'tanque' | 'tolva' | 'chasis'
  | 'reefer' | 'equipo';
export type UnitKind = BuiltinUnitKind | (string & {});
export type UnitStatus = 'active' | 'shop' | 'down' | 'sold';
export type UnitMeterKind = 'km' | 'mi' | 'hours' | 'none';
/** Estado por ítem del walkaround (vive aquí porque el contrato lo usa). */
export type InspectionItemStatus = 'ok' | 'defect' | 'na';
export interface UnitPlate { country: 'MX' | 'US' | 'CA'; number: string }

/** Tipo de unidad definido por la empresa (companies/<cid>.customUnitKinds). */
export interface CustomUnitKind { id: string; label: string; category: UnitCategory }

/** Componente permanente de una unidad — el reefer de la caja refrigerada. */
export interface UnitComponent {
  id: string; kind: string; label: string;
  brand: string; model: string; serial: string;
  meterKind: UnitMeterKind; meter: { value: number; at: string } | null;
  maintenanceMeterInterval: number | null; maintenanceDaysInterval: number | null;
  lastServiceMeter: number | null; lastServiceAt: string | null;
}
export interface UnitComponentInput {
  id?: string; kind: string; label?: string;
  brand?: string; model?: string; serial?: string;
  meterKind?: UnitMeterKind;
  maintenanceMeterInterval?: number | null; maintenanceDaysInterval?: number | null;
}

/** Unidad de la empresa: vehículo, remolque remolcado o equipo adosado. */
export interface Unit {
  id: string; kind: UnitKind; category: UnitCategory; parentUnitId: string | null;
  /** Placas por país: una unidad de cruce lleva placa MX y US. */
  plates: UnitPlate[]; internalNumber: string;
  brand: string; model: string; year: number | null; vin: string;
  owner: 'company' | 'client' | 'third';
  meterKind: UnitMeterKind; meter: { value: number; at: string } | null;
  maintenanceMeterInterval: number | null; maintenanceDaysInterval: number | null;
  lastServiceMeter: number | null; lastServiceAt: string | null;
  components: UnitComponent[];
  status: UnitStatus;
  /** Motivo y fecha del último cambio de estado (taller, fuera de servicio…). */
  statusAt: string | null; statusNote: string | null;
  /** Marca de la última inspección físico-mecánica (pre/post-viaje). */
  lastInspectionAt?: string | null; lastInspectionOutcome?: 'ok' | 'defects' | null;
  photoIds: string[]; createdAt: string; updatedAt: string;
}

export interface UnitInput {
  kind: UnitKind; plates: UnitPlate[]; internalNumber: string;
  brand: string; model: string; year: number | null; vin: string;
  owner: Unit['owner']; meterKind: UnitMeterKind; parentUnitId?: string | null;
  components?: UnitComponentInput[];
  /** Alta de tipo personalizado: el servicio lo registra en la empresa. */
  newKind?: { label: string; category: UnitCategory };
  maintenanceMeterInterval?: number | null; maintenanceDaysInterval?: number | null;
}

/** Lectura del odómetro/contador; componentId apunta al componente de la unidad (horas del reefer). */
export interface MeterReading { id: string; unitId: string; componentId: string | null; value: number; at: string; reportedBy: string; source: 'tripClose' | 'office' | 'driver' }

/** Reporte de falla/aviso sobre una unidad: lo levanta el chofer (foto opcional)
    y la oficina lo mueve entre abierta/en taller/resuelta. */
export type UnitReportSeverity = 'low' | 'medium' | 'high';
export type UnitReportStatus = 'open' | 'inShop' | 'resolved';
export interface UnitReport {
  id: string; unitId: string; componentId: string | null;
  severity: UnitReportSeverity; status: UnitReportStatus;
  description: string; photoId: string | null;
  /** Posición de llanta afectada (key de tirePositions) cuando aplica. */
  tirePosition?: string | null;
  reportedBy: string; reportedByName: string; createdAt: string;
  resolvedBy: string | null; resolvedAt: string | null; resolveNote: string;
  /** Al resolver con taller: quién reparó (llantero/taller libre), cuánto
      cobró y el servicio de unidad que se generó (kind 'tires' si el
      reporte trae tirePosition). */
  repairedBy?: string | null; repairCost?: number | null; serviceId?: string | null;
  /** Reporte levantado desde una inspección (sus defectos). */
  inspectionId?: string | null;
}
export interface UnitReportInput {
  unitId: string; componentId?: string | null;
  severity: UnitReportSeverity; description: string;
  /** Key de la posición de llanta (tirePositions) si la falla es de una llanta. */
  tirePosition?: string | null;
  photo?: { contentType: string; dataBase64: string } | null;
}

/** Servicio de mantenimiento registrado por la oficina (`companies/<cid>/unitServices`).
    Actualiza lastServiceAt/lastServiceMeter de la unidad o componente al guardar. */
export type UnitServiceKind = 'oil' | 'filters' | 'brakes' | 'tires' | 'electrical' | 'suspension' | 'reefer' | 'inspection' | 'other';
export interface UnitService {
  id: string; unitId: string; componentId: string | null; kind: UnitServiceKind;
  /** Fecha del servicio; lectura del contador en ese momento. */
  at: string | null; meterValue: number | null;
  vendor: string; cost: number | null; note: string;
  /** Aviso del siguiente servicio: por contador o por fecha (opcionales). */
  nextMeter: number | null; nextAt: string | null;
  createdBy: string; createdByName: string; createdAt: string;
}
export interface UnitServiceInput {
  unitId: string; componentId?: string | null; kind: UnitServiceKind;
  at?: string | null; meterValue?: number | null;
  vendor?: string; cost?: number | null; note?: string;
  nextMeter?: number | null; nextAt?: string | null;
}

/** Inspección físico-mecánica pre/post-viaje (`companies/<cid>/unitInspections`):
    el walkaround marcado ítem por ítem; con defectos puede abrir reporte. */
export type UnitInspectionKind = 'pretrip' | 'posttrip';
export interface UnitInspection {
  id: string; unitId: string; kind: UnitInspectionKind;
  outcome: 'ok' | 'defects';
  items: Record<string, InspectionItemStatus>;
  defects: { item: string; note: string; photoId?: string | null; photoPath?: string | null }[];
  severity: UnitReportSeverity;
  notes: string; meter: number | null;
  tripId: string | null; reportId: string | null;
  /** §396.13: en pre-viaje el chofer declara haber revisado el reporte
      anterior y la reparación de sus defectos. */
  reviewedPrevious?: boolean;
  createdBy: string; createdByName: string; createdAt: string;
}
export interface UnitInspectionInput {
  unitId: string; kind: UnitInspectionKind;
  items: Record<string, InspectionItemStatus>;
  defects: { item: string; note: string }[];
  /** Foto por defecto (≤8): solo ítems marcados defecto; viaja embebida. */
  photos?: { item: string; photo: { contentType: string; dataBase64: string } }[];
  severity?: UnitReportSeverity; notes?: string;
  meter?: number | null; tripId?: string | null;
  reviewedPrevious?: boolean;
}

export type DocumentStatus = 'pendingReview' | 'verified' | 'rejected';

/** Adjunto de un documento (imagen o PDF): se sirve por `?media=` con Bearer
    con la referencia `media:company/<cid>/doc/<docId>/<attachmentId>` (`path`). */
export interface UnitDocumentAttachment { id: string; name: string; contentType: string; path: string }

/** Sujeto de un documento con vencimiento: una unidad, un operador o la
    empresa misma (su constancia fiscal, permiso SCT, póliza de flota… también
    caducan). Con 'company' el subjectId es el id de la empresa. */
export type DocSubjectType = 'unit' | 'member' | 'company';
export interface SubjectDescriptor { type: DocSubjectType; id: string }

/** Documento genérico con vencimiento — sirve unidades (seguros, fianza…), operadores (visa, licencia…) y la empresa. */
export interface UnitDocument {
  id: string; subjectType: DocSubjectType; subjectId: string;
  type: string; country: 'MX' | 'US' | null; label: string;
  number: string; issuedAt: string | null; expiresAt: string | null;
  photoId: string | null; notes: string;
  /** Adjuntos del documento (hasta 4: imágenes o PDF). `photoId` queda por
      compat con docs viejos del snapshot. */
  attachments?: UnitDocumentAttachment[];
  uploadedBy: string; uploadedByName: string; uploadedAt: string;
  status: DocumentStatus; verifiedBy: string | null; verifiedAt: string | null;
  rejectNote: string; supersededBy: string | null; supersededAt: string | null;
}

export interface DocumentInput {
  subjectType: DocSubjectType; subjectId: string; type: string;
  country?: 'MX' | 'US' | null; label?: string; number?: string;
  issuedAt?: string | null; expiresAt?: string | null; notes?: string;
  photo?: { contentType: string; dataBase64: string } | null;
  /** Adjuntos nuevos (máx 4, ≤4.5 MB c/u; image/* o application/pdf). */
  files?: { name: string; contentType: string; dataBase64: string }[];
}

// ---- Documentos del viaje (companies/<cid>/tripDocs) ----

/** Tipo del documento del viaje. 'pod' (comprobante de entrega) lo sube el
    chofer desde la app; en el portal se lista pero no se ofrece en el alta. */
export type TripDocKind = 'cartaPorte' | 'factura' | 'nota' | 'pedimento' | 'bol' | 'pod' | 'otro';

export const TRIP_DOC_KINDS: Record<TripDocKind, string> = {
  cartaPorte: 'Carta porte',
  factura: 'Factura',
  nota: 'Nota / remisión',
  pedimento: 'Pedimento',
  bol: 'Bill of lading',
  pod: 'Comprobante de entrega (POD)',
  otro: 'Otro',
};

/** Documento adjunto a un viaje: carta porte, factura, pedimento, POD… Lo
    sube la oficina desde el portal o el chofer desde la app (solo POD). Sus
    adjuntos se sirven por media-ref `company/<cid>/tripdoc/<docId>/<attId>`
    (`att.path` ya lo trae resuelto). `stopId` es el placeId de la parada a
    la que se amarra el documento (las paradas no llevan id propio). */
export interface TripDoc {
  id: string; tripId: string; kind: TripDocKind; label: string;
  stopId?: string | null; note: string;
  attachments: UnitDocumentAttachment[];
  uploadedBy: string; uploadedByName: string; uploadedAt: string;
}

/** Llanta montada en una posición de la unidad (companies/<cid>/unitTires):
    removedAt != null la manda al historial (desmontaje con motivo). */
export type TireRemoveReason = 'worn' | 'retread' | 'flat' | 'damage' | 'stolen' | 'other';
/** Dónde quedó el casco: montada en la unidad o su destino tras desmontar
    (bodega recuperable, reencauche, desecho, robo/pérdida). Se fija al
    desmontar (o después con setTireLocation); yard+retread alimentan el
    stock remontable de listTireStock. */
export type TireLocation = 'mounted' | 'yard' | 'retread' | 'scrap' | 'gone';
export interface UnitTire {
  id: string; unitId: string;
  /** Key de la posición física (tirePositions: dir-l, t1-li, e1-re…). */
  position: string;
  brand: string; model: string; size: string;
  /** Profundidad del dibujo en 32avos de pulgada (mínimo DOT: 4 dir, 2 resto). */
  tread32: number | null;
  dot: string; retread: boolean;
  installedAt: string | null; installedMeter: number | null;
  removedAt: string | null; removedReason: TireRemoveReason | null;
  removedTread32: number | null; removedNote: string; notes: string;
  /** Ubicación del casco (docs viejos pueden no traerla: tratar como yard
      cuando removedAt != null, mounted en caso contrario). */
  location?: TireLocation | null;
  createdBy: string; createdByName: string; createdAt: string; updatedAt: string;
}
export interface UnitTireInput {
  unitId: string; position: string;
  brand?: string; model?: string; size?: string;
  tread32?: number | null; dot?: string; retread?: boolean;
  installedAt?: string | null; installedMeter?: number | null; notes?: string;
}

export interface Snapshot {
  session: Session; places: Place[]; contributions: Contribution[]; favoriteIds: string[]; members: Session[];
  activeVisit: Visit | null; myVisits: Visit[]; audit?: AuditEvent[];
  company: CompanyInfo | null;
  companyPlaces: Place[];
  companyMembers: Session[];
  myTrips: Trip[];
  companyTrips?: Trip[]; companies?: Company[];
  /** Lugares del directorio marcados como frecuentes por la empresa activa
      (oficina). Llega solo cuando el miembro tiene empresa activa. */
  companyFavoriteIds?: string[];
  /** Unidades de la empresa (miembros la ven completa); vacío sin empresa activa. */
  companyUnits?: Unit[];
  /** Documentos con vencimiento de unidades y operadores; la oficina los ve todos. */
  companyDocuments?: UnitDocument[];
  /** Reportes de falla de la flota. */
  companyUnitReports?: UnitReport[];
  /** Servicios recientes de la flota (los últimos por empresa). */
  companyUnitServices?: UnitService[];
  /** Inspecciones pre/post-viaje recientes de la flota. */
  companyUnitInspections?: UnitInspection[];
  /** Llantas activas de la flota (una por posición montada); el historial de
      desmontadas se pide con listUnitTires por unidad. */
  companyUnitTires?: UnitTire[];
}

export interface ServerStatus {
  now: string; uptimeSec: number; version?: string;
  counts: { members: number; membersActive: number; membersPending: number; places: number; visits: number; contributionsPending: number; sessionsActive: number; pushTokens: number };
}
